import React, { useState, useCallback } from 'react';
import ActivityFeed from './common/ActivityFeed';
import {
  Activity,
  FolderKanban,
  Users2,
  Building2,
  DollarSign,
  Target,
  RefreshCw,
  Plus,
  Hash,
  CalendarDays,
  Cpu,
  Lightbulb,
  LayoutGrid,
  FileText,
  Sliders,
  Sparkles,
  Search,
  Filter,
  BriefcaseBusiness,
  MessageSquare,
  ShieldCheck,
  ArrowUpRight
} from 'lucide-react';
import { useUI } from '../context/UIContext';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { usePulseFeed, useCreatePulsePost, useAddPulseReaction, useRemovePulseReaction, useAddPulseComment, useUpdatePulsePost, useDeletePulsePost } from '@/api/queries';
import { useQueries } from '@tanstack/react-query';
import { PulseService } from '../services/pulse/pulse.service';

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center">
      <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
        <Icon size={16} />
      </div>
      <span className="text-[11px] font-bold text-slate-600">{title}</span>
      <span className="mt-1 text-[10px] leading-relaxed text-slate-400">{description}</span>
    </div>
  );
}

export default function PulseView() {
  const { setCurrentView } = useUI();
  const { setGlobalFilters } = useNavigation();
  const { activeWorkspace, organization } = useAuth();
  const organizationId = organization?.id;
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>('Tudo');
  const [activeHashtagFilter, setActiveHashtagFilter] = useState<string | null>(null);
  const [feedError, setFeedError] = useState<Error | null>(null);
  const [expandedCommentPostIds, setExpandedCommentPostIds] = useState<Set<string>>(new Set());

  const CATEGORY_TO_BACKEND_MODULE: Record<string, string> = {
    'Projetos': 'PROJECTS',
    'Ideias': 'IDEAS',
    'CRM': 'CRM',
    'Tarefas': 'TASKS',
  };

  const sourceModule = CATEGORY_TO_BACKEND_MODULE[activeCategoryFilter] || undefined;
  const searchQuery = activeHashtagFilter || undefined;

  const { data: feedData, isLoading, isFetching, refetch, error } = usePulseFeed({
    organizationId,
    limit: 50,
    sourceModule,
    search: searchQuery,
  });

  // Fetch comments for expanded posts
  const commentQueries = useQueries({
    queries: Array.from(expandedCommentPostIds).map(postId => ({
      queryKey: ['pulse', 'comments', postId, organizationId],
      queryFn: () => PulseService.getComments(organizationId || '', 'pulse_post', postId),
      enabled: !!postId && !!organizationId,
      staleTime: 30_000,
    })),
  });

  const commentsMap = commentQueries.reduce((acc, query, index) => {
    const postId = Array.from(expandedCommentPostIds)[index];
    if (query.data?.items) {
      acc[postId] = query.data.items;
    }
    return acc;
  }, {} as Record<string, any[]>);

  const normalizeReactionState = (item: any) => {
    const reactionState = item?.reactions || {};
    const normalized: Record<string, number> = {};
    Object.entries(reactionState).forEach(([key, value]) => {
      if (key === 'userReactions') return;
      if (typeof value === 'number') normalized[key] = value;
    });

    return {
      ...normalized,
      userReactions: Array.isArray(reactionState.userReactions) ? reactionState.userReactions : [],
    };
  };

  const pulseFeedItems = (feedData?.items ?? []).map((item: any) => {
    const comments = Array.isArray(item.comments) ? item.comments : [];
    const content = String(item.content || item.metadata?.content || '').trim();
    const hashtags = Array.isArray(item.hashtags)
      ? item.hashtags
      : Array.isArray(item.metadata?.hashtags)
        ? item.metadata.hashtags
        : (content.match(/#[\w-]+/g) || []);

    return {
      ...item,
      id: item.sourceEntityId || item.id,
      actorId: item.actorId || item.authorId,
      authorName: item.authorName || item.author || 'Usuário',
      authorRole: item.authorRole || item.role || 'Membro',
      authorAvatar: item.authorAvatar || item.avatar || null,
      content,
      type: item.type === 'POST' ? 'comment' : item.type || 'comment',
      moduleSource: item.sourceModule || 'PULSE',
      category: item.metadata?.category || item.sourceModule || 'Geral',
      hashtags,
      reactions: normalizeReactionState(item),
      replies: comments.map((comment: any) => ({
        id: comment.id,
        author: comment.authorName || 'Usuário',
        role: comment.authorRole || 'Membro',
        time: comment.createdAt ? new Date(comment.createdAt).toLocaleString('pt-BR') : 'Agora',
        text: comment.content,
        avatar: comment.authorAvatar || null,
        replies: Array.isArray(comment.replies) ? comment.replies : [],
      })),
      isPinned: Boolean(item.isPinned),
      viewsCount: item.viewsCount || Math.max(comments.length, 1),
      timelineGroup: 'Hoje',
      company: organization?.name || activeWorkspace?.name || 'Workspace',
    };
  });
  const popularHashtags = Object.entries(
    pulseFeedItems.reduce((acc: Record<string, number>, item: any) => {
      const tags = item.hashtags || item.metadata?.hashtags || [];
      (Array.isArray(tags) ? tags : []).forEach((tag: string) => {
        const normalized = String(tag).trim();
        if (normalized) acc[normalized] = (acc[normalized] || 0) + 1;
      });
      return acc;
    }, {})
  )
    .map(([tag, count]) => [tag, Number(count)] as [string, number])
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 8);
  const pulseMetrics = [
    { id: 1, title: 'Itens no Feed', value: pulseFeedItems.length, sub: 'Dados reais carregados', icon: Activity, color: 'text-indigo-600', bg: 'bg-indigo-50', trend: 'API', trendUp: true },
    { id: 2, title: 'Publicações', value: pulseFeedItems.filter((item: any) => item.sourceEntityType === 'PULSE_POST').length, sub: 'Posts do tenant', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', trend: 'API', trendUp: true },
    { id: 3, title: 'Comentários', value: pulseFeedItems.reduce((sum: number, item: any) => sum + (item.comments?.length || 0), 0), sub: 'Interações persistidas', icon: MessageSquare, color: 'text-emerald-600', bg: 'bg-emerald-50', trend: 'API', trendUp: true },
  ];
  const recentEvents = pulseFeedItems.slice(0, 4).map((item: any) => ({
    id: item.id,
    title: item.content || 'Atualização no feed',
    meta: `${item.category || 'Pulse'} · ${item.time || 'agora'}`,
  }));
  const hasMore = feedData?.hasMore ?? false;
  const nextCursor = feedData?.nextCursor ?? null;

  const createPostMutation = useCreatePulsePost();

  const { mutate: mutateCreatePost } = createPostMutation;
  const addReactionMutation = useAddPulseReaction();
  const removeReactionMutation = useRemovePulseReaction();
  const addCommentMutation = useAddPulseComment();
  const updatePostMutation = useUpdatePulsePost();
  const deletePostMutation = useDeletePulsePost();

  const handleCategoryChange = (label: string) => {
    setActiveCategoryFilter(label);
    if (label !== 'Tudo' && !CATEGORY_TO_BACKEND_MODULE[label]) {
      setFeedError(null);
    }
  };

  const handleHashtagChange = (tag: string | null) => {
    setActiveHashtagFilter(tag);
    setFeedError(null);
  };

  const handleRetryFeed = () => {
    setFeedError(null);
    refetch();
  };

  const handlePublishPost = useCallback(async (content: string, type: string, selectedTags: string[] = []) => {
    return new Promise<void>((resolve, reject) => {
      mutateCreatePost(
        {
          content,
          visibility: 'workspace',
          workspaceId: activeWorkspace?.id ? String(activeWorkspace.id) : undefined,
          metadata: {
            type,
            sourceModule: sourceModule || 'PULSE',
            category: sourceModule || 'Geral',
            hashtags: [...new Set([...selectedTags, ...(content.match(/#[\w-]+/g) || [])].map(tag => tag.toLowerCase()))],
          },
        },
        {
          onSuccess: () => resolve(),
          onError: (err: Error) => reject(err),
        }
      );
    });
  }, [mutateCreatePost, activeWorkspace?.id, sourceModule]);

  const handleAddReaction = useCallback(async (eventId: string, reactionKey: string) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    addReactionMutation.mutate(
      { postId, dto: { type: reactionKey } },
      {
        onSuccess: () => {},
        onError: () => {
          setFeedError(new Error('Falha ao adicionar reação'));
        },
      }
    );
  }, [addReactionMutation, pulseFeedItems]);

  const handleRemoveReaction = useCallback(async (eventId: string, reactionKey: string) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    removeReactionMutation.mutate(
      { postId, dto: { type: reactionKey } },
      {
        onSuccess: () => {},
        onError: () => {
          setFeedError(new Error('Falha ao remover reação'));
        },
      }
    );
  }, [removeReactionMutation, pulseFeedItems]);

  const handleAddComment = useCallback(async (eventId: string, text: string, parentId?: string) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    addCommentMutation.mutate(
      { postId, dto: { content: text, parentId } },
      {
        onSuccess: () => {},
        onError: () => {
          setFeedError(new Error('Falha ao adicionar comentário'));
        },
      }
    );
  }, [addCommentMutation, pulseFeedItems]);

  const handleEditPost = useCallback(async (eventId: string, content: string) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    await updatePostMutation.mutateAsync({ postId, dto: { content } });
  }, [pulseFeedItems, updatePostMutation]);

  const handleDeletePost = useCallback(async (eventId: string) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    await deletePostMutation.mutateAsync({ postId });
  }, [pulseFeedItems, deletePostMutation]);

  const handleTogglePin = useCallback(async (eventId: string, pinned: boolean) => {
    const item = pulseFeedItems.find(item => item.id === eventId);
    const postId = item?.sourceEntityId || item?.id || eventId;
    await updatePostMutation.mutateAsync({ postId, dto: { isPinned: pinned } });
  }, [pulseFeedItems, updatePostMutation]);

  const handleRefreshFeed = () => {
    refetch();
  };

  const isLoadingFeed = isLoading || isFetching;

  const CATEGORIES = [
    { label: 'Tudo', icon: LayoutGrid },
    { label: 'Projetos', icon: FolderKanban },
    { label: 'Equipe', icon: Users2 },
    { label: 'Clientes', icon: Building2 },
    { label: 'Financeiro', icon: DollarSign },
    { label: 'CRM', icon: Target },
    { label: 'Ideias', icon: Lightbulb },
    { label: 'IA', icon: Cpu },
    { label: 'Sistema', icon: Sliders },
    { label: 'Eventos', icon: CalendarDays },
    { label: 'Documentos', icon: FileText }
  ];

  return (
    <div className="h-[calc(100vh-8.5rem)] min-h-0 w-full overflow-hidden rounded-[24px] border border-slate-200/70 bg-[#F8FAFC] p-3 shadow-[0_18px_30px_rgba(15,23,42,0.02)]">
      <div className="grid h-full min-h-0 grid-cols-1 gap-3 xl:grid-cols-[250px_minmax(0,1fr)_300px] xl:gap-4">
        <aside className="min-h-0 overflow-y-auto pr-1">
          <div className="flex flex-col gap-3">
            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2.5">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  </span>
                  Quem está online
                </span>
              </div>
              <EmptyState icon={Users2} title="Sem dados de presença" description="A informação de usuários online ainda não está disponível." />
            </div>

            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2.5">
                <ShieldCheck size={13} className="text-violet-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">Últimas decisões</span>
              </div>
              {(pulseFeedItems.filter((item: any) => item.sourceEntityType === 'DECISION' || item.type === 'decision' || item.type === 'approval' || item.type === 'rejection')).slice(0, 3).length > 0 ? (
                <div className="space-y-2">
                  {pulseFeedItems.filter((item: any) => item.sourceEntityType === 'DECISION' || item.type === 'decision' || item.type === 'approval' || item.type === 'rejection').slice(0, 3).map((decision: any) => (
                    <div key={decision.id || decision.sourceEntityId} className="rounded-xl border border-violet-100 bg-violet-50/30 p-2.5 transition hover:border-violet-200">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-violet-700">Decisão</span>
                        <ArrowUpRight size={12} className="text-violet-600" />
                      </div>
                      <p className="mt-1 text-[12px] font-semibold text-slate-700 line-clamp-2">{decision.content || 'Decisão registrada no workspace.'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={BriefcaseBusiness} title="Nenhuma decisão recente" description="As decisões publicadas aparecerão aqui quando existirem dados reais." />
              )}
            </div>

            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2.5">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">
                  <Hash size={13} className="text-indigo-600" />
                  Hashtags em alta
                </span>
              </div>
              {popularHashtags.length > 0 ? (
                <div className="space-y-2">
                  {popularHashtags.map(([tag, count]) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setActiveHashtagFilter(tag)}
                      className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-2.5 py-2 text-left transition hover:bg-slate-100"
                    >
                      <span className="text-[12px] font-bold text-indigo-700">#{tag}</span>
                      <span className="text-[10px] font-mono font-bold text-slate-500">{count}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Hash} title="Nenhuma hashtag disponível" description="As hashtags populares aparecerão conforme os posts forem enviados." />
              )}
            </div>
          </div>
        </aside>

        <main className="min-h-0 overflow-y-auto pr-1">
          <div className="flex flex-col gap-3 pb-4">
            <header className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_10px_24px_rgba(15,23,42,0.02)] backdrop-blur-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="text-[2rem] font-black tracking-[-0.06em] text-[#0F172A]">Pulse</h1>
                    {activeWorkspace && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {activeWorkspace.name}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Acompanhe tudo que está acontecendo no workspace.</p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRefreshFeed}
                    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 ${isFetching ? 'opacity-60' : ''}`}
                  >
                    <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
                    Atualizar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('pulse-feed-container');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F172A] px-2.5 py-2 text-[10px] font-bold text-white shadow-[0_8px_18px_rgba(15,23,42,0.18)] transition hover:bg-slate-800"
                  >
                    <Plus size={13} />
                    Nova publicação
                  </button>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                <Filter size={12} className="text-indigo-600" />
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const isSelected = activeCategoryFilter === cat.label;
                  return (
                    <button
                      key={cat.label}
                      type="button"
                      onClick={() => setActiveCategoryFilter(cat.label)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-[10px] font-bold transition ${
                        isSelected
                          ? 'border-[#0F172A] bg-[#0F172A] text-white shadow-[0_8px_16px_rgba(15,23,42,0.14)]'
                          : 'border-[#0F172A0D] bg-slate-50 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon size={11} />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </header>

            <ActivityFeed
              filterModule={CATEGORY_TO_BACKEND_MODULE[activeCategoryFilter] || undefined}
              events={pulseFeedItems}
              isLoading={isLoadingFeed}
              feedError={error instanceof Error ? error : null}
              onRetryFeed={handleRetryFeed}
              onPublishPost={handlePublishPost}
              onAddReaction={handleAddReaction}
              onRemoveReaction={handleRemoveReaction}
              onAddComment={handleAddComment}
              onTogglePin={handleTogglePin}
              onEditPost={handleEditPost}
              onDeletePost={handleDeletePost}
            />
          </div>
        </main>

        <aside className="min-h-0 overflow-y-auto pl-1">
          <div className="flex flex-col gap-3">
            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2.5">
                <Cpu size={13} className="text-cyan-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">Pulse Intelligence</span>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                <p className="text-[12px] font-semibold text-slate-700">Nenhum insight disponível</p>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                  O Copilot ainda não está conectado a esta visualização.
                </p>
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2.5">
                <Sparkles size={13} className="text-indigo-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">Atividade do workspace</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-slate-50 p-2.5 text-center">
                  <div className="text-lg font-black text-[#0F172A]">{pulseFeedItems.length}</div>
                  <div className="text-[10px] text-slate-500">Publicações</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5 text-center">
                  <div className="text-lg font-black text-[#0F172A]">{pulseFeedItems.reduce((sum: number, item: any) => sum + (item.comments?.length || 0), 0)}</div>
                  <div className="text-[10px] text-slate-500">Interações</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5 text-center">
                  <div className="text-lg font-black text-[#0F172A]">{pulseFeedItems.filter((item: any) => item.sourceEntityType === 'DECISION' || item.type === 'decision' || item.type === 'approval' || item.type === 'rejection').length}</div>
                  <div className="text-[10px] text-slate-500">Decisões</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2.5 text-center">
                  <div className="text-lg font-black text-[#0F172A]">{pulseFeedItems.filter((item: any) => item.type === 'event' || item.sourceEntityType === 'EVENT').length}</div>
                  <div className="text-[10px] text-slate-500">Eventos</div>
                </div>
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2.5">
                <ArrowUpRight size={13} className="text-indigo-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">Acesso rápido</span>
              </div>
              <div className="space-y-2 text-[12px] text-slate-700">
                <button type="button" className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-2.5 py-2 text-left transition hover:bg-slate-100"><span>Minhas publicações</span><span>↗</span></button>
                <button type="button" className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-2.5 py-2 text-left transition hover:bg-slate-100"><span>Publicações fixadas</span><span>⚡</span></button>
                <button type="button" className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-2.5 py-2 text-left transition hover:bg-slate-100"><span>Métricas do workspace</span><span>▣</span></button>
                <button type="button" className="flex w-full items-center justify-between rounded-xl bg-slate-50 px-2.5 py-2 text-left transition hover:bg-slate-100"><span>Configurações da Pulse</span><span>⚙</span></button>
              </div>
            </div>

            <div className="rounded-[18px] border border-slate-200/80 bg-white/90 p-3.5 shadow-[0_8px_18px_rgba(15,23,42,0.02)]">
              <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2.5">
                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600">
                  <CalendarDays size={13} className="text-violet-600" />
                  Eventos recentes
                </span>
              </div>
              <div className="space-y-2">
                {recentEvents.map((event: any) => (
                  <div key={event.id} className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-violet-500" />
                      <div>
                        <div className="line-clamp-2 text-[12px] font-semibold text-slate-700">{event.title}</div>
                        <div className="mt-0.5 text-[10px] text-slate-500">{event.meta}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
